/**
 * The Library's folder rail: All Documents (the flat view), Unfiled, then
 * the firm's folders as a tree with live counts. A folder opens on click,
 * expands from its chevron and offers New Subfolder, Rename, Move To… and
 * Delete Folder… from its "…" menu or a right click. Every row except All
 * Documents takes dropped documents, and a folder dragged onto another moves
 * under it. Built on the Case Files tree helpers (`folders.ts`).
 */
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderInput,
  FolderOpen,
  FolderPlus,
  Inbox,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  type LucideIcon
} from "lucide-react";
import {
  useState,
  type DragEvent,
  type FC,
  type KeyboardEvent,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  ALL_DOCUMENTS,
  UNFILED,
  canAddSubfolder,
  canMoveFolder,
  folderRows,
  type FolderTree
} from "../folders";

/** What is being dragged inside the library, for drop targets to check. */
export type LibraryDrag =
  | { kind: "entries"; ids: string[] }
  | { kind: "folder"; id: string };

/** The drag's MIME type, so the page's file-drop handler ignores it. */
export const LIBRARY_DRAG_MIME = "application/x-caseblink-library";

export const isLibraryDrag = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes(LIBRARY_DRAG_MIME);

/** Whether `drag` may land in `target` (a folder id, or null for Unfiled / the top level). */
export const canDropOn = (
  tree: FolderTree,
  drag: LibraryDrag | null,
  target: string | null
): boolean => {
  if (!drag) return false;
  if (drag.kind === "entries") return true;
  return (
    tree.byId.get(drag.id)?.parent_id !== target &&
    canMoveFolder(tree, drag.id, target)
  );
};

type FolderActions = {
  onNewFolder: (parentId: string | null) => void;
  onRename: (folderId: string) => void;
  onMove: (folderId: string) => void;
  onDelete: (folderId: string) => void;
};

type Props = FolderActions & {
  tree: FolderTree;
  /** Documents per folder, subfolders included. */
  counts: Map<string, number>;
  total: number;
  unfiled: number;
  /** `all`, `unfiled` or a folder id. */
  selected: string;
  onSelect: (scope: string) => void;
  expanded: ReadonlySet<string>;
  onToggle: (folderId: string) => void;
  canEdit: boolean;
  renamingId: string | null;
  onRenameCommit: (folderId: string, name: string) => void;
  onRenameCancel: () => void;
  drag: LibraryDrag | null;
  onDragFolder: (drag: LibraryDrag | null) => void;
  /** A drop on a folder (or on Unfiled: null). */
  onDrop: (drag: LibraryDrag, target: string | null) => void;
  className?: string;
};

const MenuItems: FC<
  FolderActions & {
    tree: FolderTree;
    folderId: string;
    Item: typeof DropdownMenuItem | typeof ContextMenuItem;
    Separator: typeof DropdownMenuSeparator | typeof ContextMenuSeparator;
  }
> = ({ tree, folderId, Item, Separator, ...actions }) => (
  <>
    <Item
      disabled={!canAddSubfolder(tree, folderId)}
      onSelect={() => actions.onNewFolder(folderId)}
    >
      <FolderPlus /> New Subfolder
    </Item>
    <Item onSelect={() => actions.onRename(folderId)}>
      <Pencil /> Rename
    </Item>
    <Item onSelect={() => actions.onMove(folderId)}>
      <FolderInput /> Move To…
    </Item>
    <Separator />
    <Item variant="destructive" onSelect={() => actions.onDelete(folderId)}>
      <Trash2 /> Delete Folder…
    </Item>
  </>
);

const RenameInput: FC<{
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}> = ({ initial, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const commit = () => {
    const name = value.trim();
    if (name && name !== initial) onCommit(name);
    else onCancel();
  };
  return (
    <Input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") onCancel();
      }}
      aria-label="Folder Name"
      className="h-6 min-w-0 flex-1 px-1.5 text-base"
      data-testid="folder-rename-input"
    />
  );
};

const RailRow: FC<{
  icon: LucideIcon;
  label: ReactNode;
  count?: number;
  depth?: number;
  selected: boolean;
  target: boolean;
  chevron?: "open" | "closed" | null;
  onToggle?: () => void;
  onClick: () => void;
  menu?: ReactNode;
  testId?: string;
  dropProps?: {
    onDragOver: (e: DragEvent<HTMLDivElement>) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent<HTMLDivElement>) => void;
  };
  dragProps?: {
    draggable: boolean;
    onDragStart: (e: DragEvent<HTMLDivElement>) => void;
    onDragEnd: () => void;
  };
}> = ({
  icon: Icon,
  label,
  count,
  depth = 0,
  selected,
  target,
  chevron,
  onToggle,
  onClick,
  menu,
  testId,
  dropProps,
  dragProps
}) => (
  <div
    role="treeitem"
    aria-selected={selected}
    aria-expanded={chevron ? chevron === "open" : undefined}
    tabIndex={0}
    data-testid={testId}
    data-drop-target={target || undefined}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    style={{ paddingLeft: 4 + depth * 14 }}
    className={cn(
      "group/rail flex h-7 min-w-0 cursor-pointer items-center gap-1.5 rounded-md pr-1 text-base text-fg-2 outline-none hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40",
      selected && "bg-fill-2 font-medium text-fg",
      target && "bg-accent-soft text-fg ring-1 ring-accent-line ring-inset"
    )}
    {...dropProps}
    {...dragProps}
  >
    <span
      className="flex size-4 shrink-0 items-center justify-center text-fg-4"
      onClick={(e) => {
        if (!chevron || !onToggle) return;
        e.stopPropagation();
        onToggle();
      }}
      aria-hidden
    >
      {chevron === "open" ? (
        <ChevronDown className="size-3" />
      ) : chevron === "closed" ? (
        <ChevronRight className="size-3" />
      ) : null}
    </span>
    <Icon
      className={cn(
        "size-4 shrink-0",
        target ? "text-accent-seed" : selected ? "text-fg" : "text-fg-3"
      )}
      aria-hidden
    />
    <span className="flex min-w-0 flex-1 items-center truncate">{label}</span>
    {menu ? (
      <span
        className="hidden shrink-0 group-hover/rail:flex has-aria-expanded:flex"
        onClick={(e) => e.stopPropagation()}
      >
        {menu}
      </span>
    ) : null}
    {count !== undefined ? (
      <span
        className={cn(
          "shrink-0 pr-1 text-sm text-fg-4 tabular-nums",
          menu && "group-hover/rail:hidden group-has-aria-expanded/rail:hidden"
        )}
      >
        {count}
      </span>
    ) : null}
  </div>
);

export const FolderRail: FC<Props> = ({
  tree,
  counts,
  total,
  unfiled,
  selected,
  onSelect,
  expanded,
  onToggle,
  canEdit,
  renamingId,
  onRenameCommit,
  onRenameCancel,
  drag,
  onDragFolder,
  onDrop,
  className,
  ...actions
}) => {
  const [over, setOver] = useState<string | null>(null);
  const rows = folderRows(tree, expanded);

  const dropFor = (target: string | null) =>
    canEdit
      ? {
          onDragOver: (e: DragEvent<HTMLDivElement>) => {
            if (!isLibraryDrag(e) || !canDropOn(tree, drag, target)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setOver(target ?? UNFILED);
          },
          onDragLeave: () => setOver(null),
          onDrop: (e: DragEvent<HTMLDivElement>) => {
            if (!isLibraryDrag(e) || !drag) return;
            e.preventDefault();
            setOver(null);
            if (canDropOn(tree, drag, target)) onDrop(drag, target);
          }
        }
      : undefined;

  return (
    <nav
      aria-label="Library Folders"
      className={cn(
        "flex min-h-0 w-58 shrink-0 flex-col overflow-y-auto border-r border-line-2 p-2",
        className
      )}
      data-testid="folder-rail"
    >
      <div role="tree" aria-label="Library Folders" className="flex flex-col">
        <RailRow
          icon={Layers}
          label="All Documents"
          count={total}
          selected={selected === ALL_DOCUMENTS}
          target={false}
          onClick={() => onSelect(ALL_DOCUMENTS)}
          testId="rail-all"
        />
        <RailRow
          icon={Inbox}
          label="Unfiled"
          count={unfiled}
          selected={selected === UNFILED}
          target={over === UNFILED}
          onClick={() => onSelect(UNFILED)}
          testId="rail-unfiled"
          dropProps={drag?.kind === "entries" ? dropFor(null) : undefined}
        />
      </div>
      <div
        className={cn(
          "mt-2 flex h-7 items-center rounded-md pr-1 pl-2",
          over === "__top__" && "bg-accent-soft"
        )}
        {...(drag?.kind === "folder" && canEdit
          ? {
              onDragOver: (e: DragEvent<HTMLDivElement>) => {
                if (!isLibraryDrag(e) || !canDropOn(tree, drag, null)) return;
                e.preventDefault();
                setOver("__top__");
              },
              onDragLeave: () => setOver(null),
              onDrop: (e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                setOver(null);
                if (canDropOn(tree, drag, null)) onDrop(drag, null);
              }
            }
          : {})}
      >
        <span className="flex-1 text-sm font-medium text-fg-3">Folders</span>
        {canEdit ? (
          <Button
            variant="ghost"
            size="icon-2xs"
            aria-label="New Folder"
            onClick={() => actions.onNewFolder(null)}
            data-testid="rail-new-folder"
          >
            <Plus />
          </Button>
        ) : null}
      </div>
      <div role="tree" aria-label="Folders" className="flex flex-col">
        {rows.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-fg-4">
            {canEdit
              ? "No folders yet. Group documents by company, research or workflow."
              : "No folders yet."}
          </p>
        ) : null}
        {rows.map(({ folder, depth, hasChildren }) => {
          const open = expanded.has(folder.id);
          const isSelected = selected === folder.id;
          const renaming = renamingId === folder.id;
          const row = (
            <RailRow
              icon={isSelected ? FolderOpen : Folder}
              depth={depth}
              label={
                renaming ? (
                  <RenameInput
                    initial={folder.name}
                    onCommit={(name) => onRenameCommit(folder.id, name)}
                    onCancel={onRenameCancel}
                  />
                ) : (
                  folder.name
                )
              }
              count={renaming ? undefined : counts.get(folder.id) ?? 0}
              selected={isSelected}
              target={over === folder.id}
              chevron={hasChildren ? (open ? "open" : "closed") : null}
              onToggle={() => onToggle(folder.id)}
              onClick={() => (renaming ? undefined : onSelect(folder.id))}
              testId="rail-folder"
              dropProps={dropFor(folder.id)}
              dragProps={
                canEdit && !renaming
                  ? {
                      draggable: true,
                      onDragStart: (e) => {
                        e.dataTransfer.setData(LIBRARY_DRAG_MIME, folder.id);
                        e.dataTransfer.effectAllowed = "move";
                        onDragFolder({ kind: "folder", id: folder.id });
                      },
                      onDragEnd: () => onDragFolder(null)
                    }
                  : undefined
              }
              menu={
                canEdit && !renaming ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-2xs"
                        aria-label={`Folder Actions for ${folder.name}`}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-52">
                      <MenuItems
                        tree={tree}
                        folderId={folder.id}
                        Item={DropdownMenuItem}
                        Separator={DropdownMenuSeparator}
                        {...actions}
                      />
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : undefined
              }
            />
          );
          return canEdit && !renaming ? (
            <ContextMenu key={folder.id}>
              <ContextMenuTrigger asChild>
                <div>{row}</div>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-52">
                <MenuItems
                  tree={tree}
                  folderId={folder.id}
                  Item={ContextMenuItem}
                  Separator={ContextMenuSeparator}
                  {...actions}
                />
              </ContextMenuContent>
            </ContextMenu>
          ) : (
            <div key={folder.id}>{row}</div>
          );
        })}
      </div>
    </nav>
  );
};
